package com.evoapps.evoshift.domain.usecase.growth

import com.evoapps.evoshift.domain.model.GrowthLinks
import com.evoapps.evoshift.domain.model.ReferralCodes
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.model.TeamJoin
import com.evoapps.evoshift.domain.repository.ReferralRepository
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map

/**
 * SH-239: the person's "Recommend EVoShift" link: their referral code, made once (random, no
 * personal data) and kept on their profile.
 */
class GetReferralLinkUseCase(
    private val repository: ReferralRepository,
    private val newCode: () -> String = { ReferralCodes.generate() }
) {
    suspend operator fun invoke(uid: String): Result<String> = runCatching {
        val code = repository.get(uid).getOrThrow()?.takeIf { ReferralCodes.isValid(it) }
            ?: repository.createIfMissing(uid, newCode()).getOrThrow()
        GrowthLinks.referral(code)
    }
}

/**
 * SH-239: [TeamJoin] of a workplace, again whenever whether a month is published changes; the team
 * is read then (and on each new collection of the flow).
 */
class ObserveTeamJoinUseCase(
    private val userRepository: UserRepository,
    private val workplaceRepository: WorkplaceRepository,
    private val rosterRepository: RosterRepository
) {
    operator fun invoke(workplaceId: String): Flow<TeamJoin> =
        rosterRepository.observeRosters(workplaceId).catch { emit(emptyList()) }
            .map { rosters -> rosters.any { it.status in PUBLISHED } }
            .distinctUntilChanged()
            .map { published -> teamJoin(workplaceId, published) }

    private suspend fun teamJoin(workplaceId: String, hasPublished: Boolean): TeamJoin {
        if (!hasPublished) return TeamJoin(hasPublished = false, total = 0, joined = 0)
        val ownerId = workplaceRepository.getWorkplace(workplaceId).getOrNull()?.ownerId
        val team = userRepository.getAssignableEmployees(workplaceId).getOrNull().orEmpty()
            .filter { it.uid != ownerId && it.engagements[workplaceId]?.isActive == true }
        return TeamJoin(hasPublished = true, total = team.size, joined = team.count { !it.isShadow })
    }

    private companion object {
        val PUBLISHED = setOf(RosterStatus.PUBLISHED, RosterStatus.LOCKED, RosterStatus.ARCHIVED)
    }
}
